import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { contactAPI, lookupAPI } from '../services/api';
import { HEARING_STATUS_OPTIONS, INTERPRETATION_STATUS_OPTIONS } from '../constants/contactOptionSets';
import './ContactForm.css';
import './ContactRelatedClients.css';

function ContactForm() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const pathname = location?.pathname || '';
  const isNestedUnderClient = pathname.includes('/client/') && pathname.includes('/contact/');
  const id = isNestedUnderClient ? params.contactId : params.id;
  const returnToClientId = isNestedUnderClient ? params.id : (location.state?.returnToClientId ?? null);
  const returnToPath = location.state?.returnToPath ?? null;
  const isEdit = !!id && id !== 'new';

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    street_1: '',
    street_2: '',
    city: '',
    city_id: '',
    state: 'IL',
    zip: '',
    county_id: '',
    phone: '',
    other_phone: '',
    email: '',
    preferred_language_id: '',
    hearing_status: 'hearing',
    interpretation_status: 'not_needed',
    primary_language_id: '',
  });

  const [languages, setLanguages] = useState([]);
  const [cities, setCities] = useState([]);
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [relatedClients, setRelatedClients] = useState([]);
  const [relatedClientsLoading, setRelatedClientsLoading] = useState(false);

  useEffect(() => {
    lookupAPI.getLanguages()
      .then((res) => {
        const list = res?.data?.data || res?.data || [];
        setLanguages(list);
        // Default Preferred Language and Primary Language to English when adding a new contact
        if (!id && list?.length) {
          const english = list.find((l) => (l.description || '').trim().toLowerCase() === 'english');
          if (english) {
            setFormData((prev) =>
              prev.preferred_language_id === '' && prev.primary_language_id === ''
                ? { ...prev, preferred_language_id: String(english.id), primary_language_id: String(english.id) }
                : prev
            );
          }
        }
      })
      .catch(() => setLanguages([]));
    lookupAPI.getCities()
      .then((res) => setCities(res?.data?.data || res?.data || []))
      .catch(() => setCities([]));
    lookupAPI.getCounties()
      .then((res) => setCounties(res?.data?.data || res?.data || []))
      .catch(() => setCounties([]));
  }, [id]);

  useEffect(() => {
    if (isEdit) {
      loadContact();
    }
  }, [id]);

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    setRelatedClientsLoading(true);
    contactAPI.getRelatedClients(id)
      .then((response) => {
        if (!cancelled) {
          const data = response.data?.data;
          setRelatedClients(data?.relatedClients || []);
        }
      })
      .catch(() => {
        if (!cancelled) setRelatedClients([]);
      })
      .finally(() => {
        if (!cancelled) setRelatedClientsLoading(false);
      });
    return () => { cancelled = true; };
  }, [isEdit, id]);

  const loadContact = async () => {
    try {
      setLoading(true);
      const response = await contactAPI.getById(id);
      const d = response.data.data;
      setFormData({
        first_name: d.first_name || '',
        last_name: d.last_name || '',
        street_1: d.street_1 || '',
        street_2: d.street_2 || '',
        city: d.city || '',
        city_id: d.city_id || '',
        state: d.state || '',
        zip: d.zip || '',
        county_id: d.county_id != null ? String(d.county_id) : '',
        phone: d.phone || '',
        other_phone: d.other_phone || '',
        email: d.email || '',
        preferred_language_id: d.preferred_language_id ?? '',
        hearing_status: d.hearing_status || '',
        interpretation_status: d.interpretation_status || '',
        primary_language_id: d.primary_language_id ?? '',
      });
    } catch (error) {
      console.error('Error loading contact:', error);
      alert('Failed to load contact');
      navigate(returnToPath ? returnToPath : tenantPath(returnToClientId ? `/client/${returnToClientId}` : '/lists/contacts'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const updates = { [e.target.name]: e.target.value };
    // Keep legacy city text in sync with city_id so search display shows the correct city
    if (e.target.name === 'city_id') {
      const selected = cities.find((c) => String(c.id) === e.target.value);
      updates.city = selected ? selected.city : formData.city;
    }
    setFormData({ ...formData, ...updates });
    setIsDirty(true);
  };

  const performSave = useCallback(async () => {
    if (isEdit) {
      await contactAPI.update(id, formData);
    } else {
      await contactAPI.create(formData);
    }
  }, [formData, isEdit, id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      await performSave();
      setIsDirty(false);
      navigate(returnToPath ? returnToPath : tenantPath(returnToClientId ? `/client/${returnToClientId}` : '/lists/contacts'));
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return <div className="loading">Loading contact...</div>;
  }

  const backTo = returnToPath ? returnToPath : (returnToClientId ? tenantPath(`/client/${returnToClientId}`) : tenantPath('/lists/contacts'));

  return (
    <div className="contact-form">
      <div className="contact-form-header">
        <div>
          <Link to={backTo} className="back-link">← Back</Link>
          <h1>{isEdit ? 'Edit Contact' : 'Add New Contact'}</h1>
        </div>
        {isEdit && (
          <div className="contact-form-header-actions">
            <Link to={backTo} className="btn-cancel">Cancel</Link>
            <button type="submit" form="contact-form" disabled={loading || !isDirty} className="btn-save">
              {loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <form id="contact-form" onSubmit={handleSubmit}>
        <div className="contact-form-grid">
          <div className="form-group">
            <label>First Name *</label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Last Name *</label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Street Address 1</label>
            <input
              type="text"
              name="street_1"
              value={formData.street_1}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Street Address 2</label>
            <input
              type="text"
              name="street_2"
              value={formData.street_2}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>City</label>
            <select
              name="city_id"
              value={formData.city_id || ''}
              onChange={handleChange}
            >
              <option value="">-- Select City --</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.city}
                </option>
              ))}
            </select>
            {!formData.city_id && formData.city && String(formData.city).trim() !== '' && (
              <p className="form-hint" style={{ marginTop: '0.35rem', fontSize: '0.9em', color: '#555' }}>
                Imported city (not linked to lookup yet): <strong>{formData.city}</strong>.
                Pick the matching city above to link it.
              </p>
            )}
          </div>

          <div className="form-group">
            <label>State</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              maxLength="2"
              placeholder="IL"
            />
          </div>

          <div className="form-group">
            <label>ZIP Code</label>
            <input
              type="text"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              maxLength="10"
              placeholder="60601"
            />
          </div>

          <div className="form-group">
            <label>County</label>
            <select
              name="county_id"
              value={formData.county_id || ''}
              onChange={handleChange}
            >
              <option value="">-- Select County --</option>
              {counties.map((county) => (
                <option key={county.id} value={county.id}>
                  {county.county}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Phone *</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Other phone</label>
            <input
              type="tel"
              name="other_phone"
              value={formData.other_phone}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Preferred Language</label>
            <select
              name="preferred_language_id"
              value={formData.preferred_language_id}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              {languages.map((lang) => (
                <option key={lang.id} value={lang.id}>
                  {lang.description}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Primary Language</label>
            <select
              name="primary_language_id"
              value={formData.primary_language_id}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              {languages.map((lang) => (
                <option key={lang.id} value={lang.id}>
                  {lang.description}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Hearing Status</label>
            <select
              name="hearing_status"
              value={formData.hearing_status}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              {HEARING_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Interpretation Status</label>
            <select
              name="interpretation_status"
              value={formData.interpretation_status}
              onChange={handleChange}
            >
              <option value="">Select...</option>
              {INTERPRETATION_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-actions">
          <Link to={backTo} className="btn-cancel">Cancel</Link>
          <button type="submit" disabled={loading || (isEdit && !isDirty)} className="btn-save">
            {loading ? 'Saving…' : isEdit ? 'Save' : 'Create contact'}
          </button>
        </div>
      </form>

      {isEdit && (
        <section className="contact-form-related-clients">
          <h2>Related clients</h2>
          {relatedClientsLoading ? (
            <p className="related-clients-loading">Loading related clients…</p>
          ) : relatedClients.length === 0 ? (
            <p className="related-clients-empty">This contact is not linked as primary or secondary contact for any client.</p>
          ) : (
            <div className="related-clients-table-wrap">
              <table className="related-clients-table">
                <thead>
                  <tr>
                    <th>Client name</th>
                    <th>EI number</th>
                    <th>Relationship</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {relatedClients.map((row) => (
                    <tr key={`${row.client_id}-${row.relationship_type}`}>
                      <td>{row.last_name}, {row.first_name}</td>
                      <td>{row.ei_number ?? '—'}</td>
                      <td>{row.relationship_description ?? '—'}</td>
                      <td>
                        <Link to={tenantPath(`/client/${row.client_id}`)} className="link-nav">
                          View client
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default ContactForm;
